const END = 0xc0;
const ESC = 0xdb;
const ESC_END = 0xdc;
const ESC_ESC = 0xdd;

/**
 * One SLIP packet, decoded as its bytes arrive. Each byte is copied once:
 * esptool-js's own decoder builds a new array for every byte, so a 4 KB
 * packet of a flash read costs megabytes of copying and a 1 MB read times out.
 */
export class SlipDecoder {
    #packet: Uint8Array | undefined;
    #length = 0;
    #escaping = false;

    /** Whether the packet's first END has arrived. */
    get started(): boolean {
        return this.#packet !== undefined;
    }

    /**
     * Take `bytes`; when they end the packet, the packet and the bytes after it.
     * Throws, as esptool-js does, on bytes that are not SLIP.
     */
    push(bytes: Uint8Array): { packet: Uint8Array; rest: Uint8Array } | undefined {
        for (let i = 0; i < bytes.length; i++) {
            const byte = bytes[i]!;
            if (this.#packet === undefined) {
                if (byte !== END) {
                    throw new Error(
                        `Invalid head of packet (0x${byte.toString(16)}): Possible serial noise or corruption.`,
                    );
                }
                this.#packet = new Uint8Array(256);
            } else if (this.#escaping) {
                this.#escaping = false;
                if (byte === ESC_END) this.#append(END);
                else if (byte === ESC_ESC) this.#append(ESC);
                else throw new Error(`Invalid SLIP escape (0xdb, 0x${byte.toString(16)})`);
            } else if (byte === ESC) {
                this.#escaping = true;
            } else if (byte === END) {
                return { packet: this.#packet.slice(0, this.#length), rest: bytes.subarray(i + 1) };
            } else {
                this.#append(byte);
            }
        }
        return undefined;
    }

    #append(byte: number): void {
        if (this.#length === this.#packet!.length) {
            const grown = new Uint8Array(this.#packet!.length * 2);
            grown.set(this.#packet!);
            this.#packet = grown;
        }
        this.#packet![this.#length++] = byte;
    }
}
